import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import type { Color } from "chroma-js"
import { ChevronRight, Map } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  SIZE_SCALE_OPTIONS,
  normalizedToExamined,
  type SizeScale,
} from "./ParasiteRateMap"

/**
 * Options for the color-by column dropdown. Each entry maps a field key
 * from ParasiteRatePoint to a display label.
 */
export interface ColorColumnOption {
  /** The field key from ParasiteRatePoint (e.g. "pf_positive_rate"). */
  value: string
  /** Human-readable label for the dropdown. */
  label: string
}

/**
 * Available columns to color the map by.
 */
export const COLOR_COLUMN_OPTIONS: ColorColumnOption[] = [
  { value: "any_positive_rate", label: "Any Species Rate" },
  { value: "computed_total_pf_positive_rate", label: "Pf Parasite Rate" },
  { value: "computed_total_pv_positive_rate", label: "Pv Parasite Rate" },
  { value: "computed_total_pk_positive_rate", label: "Pk Parasite Rate" },
  { value: "computed_total_po_positive_rate", label: "Po Parasite Rate" },
  { value: "computed_total_pm_positive_rate", label: "Pm Parasite Rate" },
  { value: "pan_positive_rate", label: "Pan Parasite Rate" },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Generate a CSS linear-gradient string from a chroma color scale.
 *
 * @param colorScale - A chroma scale function that maps [0,1] to colors.
 * @returns A CSS linear-gradient value for use in a style prop.
 */
function buildGradientCSS(colorScale: (t: number) => Color): string {
  const stops = 10
  const colors: string[] = []
  for (let i = 0; i <= stops; i++) {
    colors.push(colorScale(i / stops).hex())
  }
  return `linear-gradient(to right, ${colors.join(", ")})`
}

/** Number of concentric rings shown in the "examined" size legend. */
const EXAMINED_RING_COUNT = 5

/**
 * Diameter (in pixels) of the largest legend circle — the one representing the
 * dataset's maximum examined value.
 */
const MAX_CIRCLE_DIAMETER_PX = 32

/**
 * Diameter (in pixels) of the smallest legend circle (the 0-examined end).
 * Kept small but non-zero so the smallest circle stays visible in the row.
 */
const MIN_CIRCLE_DIAMETER_PX = 4

/**
 * Describes a single circle in the examined-size legend.
 */
interface ExaminedRing {
  /** Rendered diameter of the circle, in pixels. */
  diameterPx: number
  /** The examined sample-size value this circle represents. */
  examined: number
}

/**
 * Build the set of reference circles for the "# Examined" size legend.
 *
 * @param maxExamined - The maximum examined value across the dataset. When 0 or
 *   negative (no data yet), an empty list is returned.
 * @param sizeScale - The active size-scale function, used to derive the
 *   examined count each evenly-spaced circle represents.
 * @returns An array of {@link ExaminedRing} ordered from smallest to largest.
 */
function buildExaminedRings(maxExamined: number, sizeScale: SizeScale): ExaminedRing[] {
  if (maxExamined <= 0) {
    return []
  }

  const rings: ExaminedRing[] = []
  for (let i = 0; i < EXAMINED_RING_COUNT; i++) {
    const normalized: number = i / (EXAMINED_RING_COUNT - 1)
    // Circles are spaced evenly by size, so the examined value each one labels
    // is recovered by inverting the active scale.
    const examined: number = Math.round(
      normalizedToExamined(normalized, maxExamined, sizeScale),
    )
    // Interpolate the diameter linearly between the min and max circle sizes.
    const diameterPx: number =
      MIN_CIRCLE_DIAMETER_PX + normalized * (MAX_CIRCLE_DIAMETER_PX - MIN_CIRCLE_DIAMETER_PX)
    rings.push({ diameterPx, examined })
  }

  return rings
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface MapLegendProps {
  /** The chroma color scale used by the map layer. */
  colorScale: (t: number) => Color
  maxExamined: number
  /** The currently active size-scale function driving circle sizes. */
  sizeScale: SizeScale
  /** Callback when the user selects a different size-scale function. */
  onSizeScaleChange: (scale: SizeScale) => void
  /** Whether the map is in validation-error mode. */
  errorModeActive: boolean
  /** Extra classes merged onto the root, e.g. to reposition on mobile. */
  className?: string
}

/**
 * MapLegend renders a fixed-position overlay in the bottom-left corner of the
 * map showing:
 * 1. A dropdown to select which column the map is coloured by
 * 2. A horizontal colour gradient bar representing the 0–1 value range
 *
 * The color scale is passed in as a prop so the map layer and legend always
 * stay in sync.
 *
 *
 * @param props - The selected column, change handler, color scale, the maximum
 *   examined value used to label the size rings, the size-scale selection, and
 *   whether the map is in validation-error mode.
 * @returns A positioned legend element overlaid on the map.
 */
export default function MapLegend({ colorScale, maxExamined, sizeScale, onSizeScaleChange, errorModeActive, className }: MapLegendProps): React.JSX.Element {
  const gradientCSS = buildGradientCSS(colorScale)

  const examinedRings: ExaminedRing[] = buildExaminedRings(maxExamined, sizeScale)

  return (
    <Collapsible
      defaultOpen
      className={cn(
        "absolute bottom-10 left-4 w-49 rounded-xl border border-border/60 bg-background/80 shadow-xl ring-1 ring-black/5 backdrop-blur-md z-10 overflow-hidden",
        className,
      )}
    >
      <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 px-4 py-2 text-left transition-colors hover:bg-muted/40">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-foreground/80">
          <Map className="size-3.5 mb-0.5 text-muted-foreground" aria-hidden="true" />
          Legend
        </span>
        <ChevronRight className="size-3.5 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-90" />
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="space-y-4 border-t border-border/60 px-4 pb-4 pt-3">
          {errorModeActive ? (
            <div className="flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/5 px-2.5 py-2">
              <span
                aria-hidden="true"
                className="inline-block size-3.5 shrink-0 rounded-full border-2"
                style={{
                  backgroundColor: "rgba(220, 38, 38, 0.16)",
                  borderColor: "rgb(220, 38, 38)",
                }}
              />
              <span className="text-xs font-medium text-red-600 dark:text-red-400">
                Validation failures
              </span>
            </div>
          ) : (
            <div className="space-y-2.5">
              <div>
                <div
                  className="h-3.5 w-full rounded-full shadow-inner ring-1 ring-inset ring-black/10"
                  style={{ background: gradientCSS }}
                />
                <div className="mt-1 flex justify-between text-[10px] tabular-nums text-muted-foreground">
                  <span>0</span>
                  <span>0.5</span>
                  <span>1</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className="inline-block size-3.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10"
                  style={{ backgroundColor: "rgba(128, 128, 128, 0.63)" }}
                />
                <span className="text-xs text-muted-foreground">No data for selected rate</span>
              </div>
            </div>
          )}

          {/* Radius / examined indicator */}
          <div className="space-y-2.5 border-t border-border/60 pt-3">
            <p className="text-[8px] font-semibold uppercase tracking-wider text-muted-foreground mb-0">
              Marker size · # examined
            </p>

            {examinedRings.length === 0 ? (
              <span className="text-[10px] text-muted-foreground">No data</span>
            ) : (
              <div
                className="flex items-end justify-between gap-1 rounded-md bg-muted/30 px-2 py-2"
                role="img"
                aria-label={`Circle size legend: circle sizes range from ${examinedRings[0].examined} to ${examinedRings[examinedRings.length - 1].examined} examined`}
              >
                {examinedRings.map(ring => (
                  <div
                    key={ring.examined}
                    className="flex flex-col items-center gap-1"
                    aria-hidden="true"
                  >
                    <div
                      className="rounded-full ring-1 ring-inset ring-black/10"
                      style={{
                        width: `${ring.diameterPx}px`,
                        height: `${ring.diameterPx}px`,
                        backgroundColor: "rgba(128, 128, 128, 0.15)",
                      }}
                    />
                    <span className="text-[9px] leading-none text-muted-foreground tabular-nums">
                      {ring.examined.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-row items-baseline gap-2 justify-between">
              <p className="text-[8px] font-semibold uppercase tracking-wider text-muted-foreground">
                Size-scale
              </p>
              <Select
                value={sizeScale}
                onValueChange={value => onSizeScaleChange(value as SizeScale)}
              >
                <SelectTrigger className="h-6! p-1 text-xs grow">
                  <SelectValue placeholder="Select scale..." />
                </SelectTrigger>
                <SelectContent
                  position="popper"
                  side="top"
                  align="start"
                  sideOffset={4}
                  className="max-h-40 min-w-48"
                >
                  {SIZE_SCALE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
